import styled from 'styled-components';

/*
 * Layout only. Deliberately NOT importing `AddOnsContainer` from the shipped in-checkout page: its
 * `padding: 0 16px 130px 16px` reserves 130px for that page's fixed <Footer>, which this step does not use.
 */
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  /*
   * The scroll, and it is load-bearing (RES-1302). height:100% resolves against ModalContainer, a
   * fixed 528px on desktop. Without overflow-y the excess does not hide — it PAINTS OUTSIDE the box
   * over whatever is beneath, and CtaBar's margin-top:auto lands the submit control below the visible
   * area. No ancestor scrolls; one carries overflow-y:hidden. Measured: 528 against 766.
   *
   * Not on ModalContainer: shared with the payment-iframe flows, whose layout needs its fixed size.
   */
  overflow-y: auto;

  /*
   * One gutter, owned here and retunable by the container: the desktop modal takes the receipt's
   * roomier frame, the mobile sheet keeps 16. Putting padding on the modal instead would STACK on
   * this one — the double-gutter defect this page already shipped once.
   */
  padding: var(--step-gutter-y, 16px) var(--step-gutter-x, 16px);
  box-sizing: border-box;
`;

/*
 * VIS-2193 — the picker with a pinned footer (behind `show-product-images`): the menu scrolls, and the
 * estimate and the CTA stay in view below it instead of the total sitting above the menu and the CTA
 * scrolling away under it.
 *
 * ⛔ THE PAGE STOPS SCROLLING; ITS MIDDLE DOES. A footer can only stay put if something between it and
 * the top owns a definite height and the scroll happens inside that. Below the breakpoint the sheet
 * already gives the page one (`StepContent` makes it a flex child with `min-height: 0`). Above it,
 * `StepContent` only has `max-height: 70vh` and the page's `height: 100%` resolves to auto, so the
 * scroll used to happen in `StepContent` and took the CTA with it. `max-height: inherit` hands the
 * page that same 70vh cap (and `none` on the sheet, where the flex sizing does the job instead), so in
 * both layouts the page is bounded and `StepScroll` is the one scrollport.
 *
 * The bottom gutter moves to the footer, which owns the page's last edge now.
 */
export const PageWithFooter = styled(PageContainer)`
  overflow: hidden;
  max-height: inherit;
  padding-bottom: 0;
`;

/* The menu: the only thing on the page that scrolls. Bleeds to the page edge so its scrollbar does. */
export const StepScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  margin: 0 calc(-1 * var(--step-gutter-x, 16px));
  padding: 0 var(--step-gutter-x, 16px) 16px;
`;

/* The estimate and the CTA, under a rule, never scrolled away. Same edge-to-edge bleed as the menu. */
export const StepFooter = styled.div`
  flex: 0 0 auto;
  margin: 0 calc(-1 * var(--step-gutter-x, 16px));
  padding: 12px var(--step-gutter-x, 16px) var(--step-gutter-y, 16px);
  border-top: 1px solid var(--border, #e5e7eb);
  background: var(--background, #ffffff);
`;

/*
 * An <h1>, matching every sibling page; this page shipped as a <div>. Party headers below are <h2>s,
 * so the step has a heading outline a screen reader can navigate.
 */
export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.fontColor};
  /* No horizontal padding: PageContainer owns the gutter, and this sits after a back arrow. */
  padding: 0;
  margin: 0;
`;

export const AddOnsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Vertical rhythm only; PageContainer owns the gutter. */
  padding: 16px 0;
`;

export const CtaBar = styled.div`
  margin-top: auto;
  /* Vertical rhythm only; PageContainer owns the gutter. */
  padding: 16px 0 0;
`;

export const Cta = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.universal.white};
  background: ${({ theme }) => theme.colors.$primary};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/*
 * RES-1336 — the CTA's busy mark, carried on the button rather than on a screen of its own.
 *
 * ⛔ THE LABEL STAYS. The button keeps saying what it is doing ("Submit · $50.00 estimated") with the
 * spinner beside it, because the wait here spans TWO things the tenant cannot distinguish and should
 * not have to: the order being written, and billing opening a session to charge for it. A spinner
 * that replaced the label would read as a different control appearing mid-tap.
 *
 * ⚠️ `aria-hidden`: the button is already `aria-busy` and `disabled`, which is what a screen reader
 * announces. The glyph would otherwise be a second, wordless announcement of the same state.
 */
export const CtaSpinner = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

/*
 * RES-1336 — "the charge is on its way", shown on the confirmation while billing opens a session.
 *
 * ⚠️ Quiet on purpose. The order IS placed by the time this renders and the heading above has said
 * so; this is the three asynchronous hops between that and the payment sheet, not an unfinished
 * state. Styled as a note rather than a banner so it cannot read as a problem with the order.
 */
export const AwaitingPaymentNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
`;

/*
 * The reassurance above the action, in the slot the submit error already occupies. Muted rather than
 * coloured: it qualifies the button, it does not warn about it.
 */
export const CtaNote = styled.div`
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.greys.adminGreyDark};
  margin-bottom: 10px;
`;

export const SubmitError = styled.div`
  font-family: ${({ theme }) => theme.fonts.join()};
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.$sunsetOrange ?? theme.colors.fontColor};
  margin-bottom: 10px;
`;
